/**
 * Persistent yellow banner reminding the franchisee to complete their
 * Business Details. Mounted at the top of every partner-portal page via
 * `PartnerShell` (Slice B — Feb 2026 July).
 *
 * Hidden when:
 *   - loading (avoids a flash)
 *   - business_details_status === "complete"
 *   - the current route is already /partner/business-details
 */
import { Link, useLocation } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { useOnboardingStatus } from "@/lib/onboardingStatus";

export default function BusinessDetailsBanner() {
  const { status, loading } = useOnboardingStatus();
  const loc = useLocation();

  if (loading) return null;
  if (status === "complete") return null;
  if (loc.pathname.startsWith("/partner/business-details")) return null;

  const isIncomplete = status === "incomplete";
  return (
    <div
      data-testid="business-details-banner"
      className="border-b border-amber-200 bg-amber-50"
      role="status"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2 text-amber-900 text-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-none" />
          <div>
            <b>Finish your Business Details.</b>{" "}
            {isIncomplete
              ? "A few required fields are missing (legal name, ABN, GST, physical & postal address)."
              : "We need your legal business name, ABN, GST status and addresses before we can set up payouts."}
          </div>
        </div>
        <Link
          to="/partner/business-details"
          data-testid="business-details-banner-cta"
          className="inline-flex items-center gap-2 text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-full px-4 py-1.5 transition-colors"
        >
          Complete now →
        </Link>
      </div>
    </div>
  );
}
